import { useEffect, useRef, useState, useCallback } from "react";

/**
 * WebSocket hook that connects to the backend and handles reconnection.
 * The backend accepts either the http cookie (access_token/session_token)
 * automatically, since WebSocket sends cookies for same-origin.
 */
export function useChatSocket({ enabled = true, onMessage }) {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const [connected, setConnected] = useState(false);
  const messageHandler = useRef(onMessage);

  useEffect(() => {
    messageHandler.current = onMessage;
  }, [onMessage]);

  const startPingLoop = useCallback(() => {
    // Send a `{type:"ping"}` every 25s so intermediate proxies (Cloudflare,
    // nginx, K8s ingress — all common idle-timeout ~60s) don't drop the
    // socket during long-running voice/video calls. Backend replies with pong.
    clearInterval(pingTimer.current);
    pingTimer.current = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
      }
    }, 25000);
  }, []);

  const stopPingLoop = useCallback(() => {
    clearInterval(pingTimer.current);
    pingTimer.current = null;
  }, []);

  const connect = useCallback(() => {
    if (!enabled) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    // Convert http(s) → ws(s)
    const wsUrl = backend.replace(/^http/, "ws") + "/api/ws";

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        startPingLoop();
      };
      ws.onclose = () => {
        setConnected(false);
        stopPingLoop();
        if (enabled) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = setTimeout(connect, 2000);
        }
      };
      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          messageHandler.current?.(data);
        } catch {}
      };
    } catch {
      setConnected(false);
    }
  }, [enabled, startPingLoop, stopPingLoop]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      stopPingLoop();
      const ws = wsRef.current;
      if (ws) {
        try {
          ws.onclose = null;
          ws.close();
        } catch {}
      }
    };
  }, [connect, stopPingLoop]);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
    }
  }, []);

  return { connected, send };
}
